// src/app/components/shared/confirm-dialog.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// This replaces `components/ui/confirm-dialog.tsx`, which was a thin wrapper
// around shadcn's AlertDialog. It keeps the SAME props, so the nine screens
// that use it move by changing one import line each.
//
// It is here rather than edited in place because `components/ui/*` is not
// rewritten in place (D22): a file already on the design-lint baseline carries
// its debt, and a new file is held to zero from its first line. This one is.
//
// What the kit's `Modal` brings that the wrapper did not have:
//
//   * Item 56's keyboard note, which the wrapper contradicted: "Focus opens on
//     the CANCEL control for destructive actions." shadcn's AlertDialog opens
//     focus on the content and then on the first focusable, which is Cancel by
//     DOM accident rather than by rule. Here it is a ref and it is conditional
//     on `destructive`, which is what the specimen actually says.
//   * One scrim at a time. A confirm opened from a drawer raises the existing
//     scrim rather than stacking a second (item 64, DECISIONS.md D33).
//   * Escape closes the TOP layer only, through the provider's stack.
//
// The wrapper's own comment said it "replaces native window.confirm() so
// modals match the rest of the design system". They do now.
import { useRef, type ReactNode } from "react";
import { Button, Modal } from "../kit";

export interface ConfirmDialogProps {
  /** Controls visibility. Parent owns this state. */
  open: boolean;
  /** Fired when the dialog closes (cancel, outside click, or after confirm). */
  onOpenChange: (open: boolean) => void;
  /** Fired when the user clicks the confirm action. */
  onConfirm: () => void;
  title: string;
  description?: ReactNode;
  /** Confirm button label. Default: "Confirm" */
  confirmLabel?: string;
  /** Cancel button label. Default: "Cancel" */
  cancelLabel?: string;
  /** Styles the confirm button as destructive when true. Default: false */
  destructive?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
}: ConfirmDialogProps) {
  const cancel = useRef<HTMLButtonElement | null>(null);

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={title}
      description={description}
      // Item 56: focus opens on cancel for a destructive action, so the
      // Enter key a user is already holding does not confirm it.
      initialFocus={destructive ? cancel : undefined}
      actions={
        <>
          <Button ref={cancel} variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "primary"}
            onClick={() => { onConfirm(); onOpenChange(false); }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
